import {
  EmployeeLevelEnum,
  CareerPathEvaluationState,
  Country,
  Currency,
  SkillEmployeeStatusEnum,
  SkillEmployeeStatusDto,
} from '../../types'
import { CompanySettingsEmployeeTableData } from '../companySettings/employees/EmployeesView'
import { SubordinateEmployeeTableData } from './EmployeesView'

export interface EmployeeModalFormDataType {
  country?: Country
  employeeName?: string
  office?: string
  startDate: {
    dateAfter?: string
    dateBefore?: string
  }
  endDate: {
    dateAfter?: string
    dateBefore?: string
  }
  salary: {
    lowerLimit?: number
    upperLimit?: number
  }
  targetBonus: {
    lowerLimit?: number
    upperLimit?: number
  }
  resultScore?: number
  currency?: Currency
}

export const emptyFormData: EmployeeModalFormDataType = {
  country: undefined,
  employeeName: undefined,
  office: undefined,
  startDate: {
    dateAfter: '',
    dateBefore: '',
  },
  endDate: {
    dateAfter: '',
    dateBefore: '',
  },
  salary: {
    lowerLimit: undefined,
    upperLimit: undefined,
  },
  targetBonus: {
    lowerLimit: undefined,
    upperLimit: undefined,
  },
  resultScore: undefined,
  currency: undefined,
}

export const NO_OFFICE_FILTER_VALUE = 'NO_OFFICE'

export const possibleOffices = (employees: { branchOfficeCode?: string | null }[]): string[] => {
  const codes = [...new Set(employees.map((employee) => employee.branchOfficeCode).filter((code): code is string => !!code))].sort(
    (officeA, officeB) => officeA.localeCompare(officeB),
  )
  if (employees.some((employee) => !employee.branchOfficeCode)) {
    codes.push(NO_OFFICE_FILTER_VALUE)
  }
  return codes
}

/*
 * filters table (in case of EmployeeList: subordinates) data with given filterValues
 * @param tableData
 * @param filterValues
 */

export const filterTableData = (tableData: any[], filterValues?: EmployeeModalFormDataType): any[] => {
  if (!filterValues) return tableData
  let filteredData = [...tableData]
  if (!filterValues.currency) {
    filterValues.salary = { lowerLimit: undefined, upperLimit: undefined }
    filterValues.targetBonus = { lowerLimit: undefined, upperLimit: undefined }
  }
  Object.keys(filterValues).forEach((key) => {
    const currentValue = filterValues[key]
    if (currentValue !== undefined && currentValue !== '') {
      if (key === 'employeeName') {
        filteredData = filteredData.filter((employee) => {
          return `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(currentValue.toLowerCase())
        })
      } else if (key === 'level' && currentValue !== undefined) {
        const levelKey = tableData.length && tableData[0].hasOwnProperty('currentLevel') ? 'currentLevel' : 'contractLevel'
        filteredData = filteredData.filter((employee) => employee[levelKey].toLowerCase() === currentValue.toLowerCase())
      } else if (key === 'country' && currentValue !== undefined) {
        filteredData = filteredData.filter((employee) => employee.country.toLowerCase() === currentValue.toLowerCase())
      } else if (key === 'office') {
        filteredData =
          currentValue === NO_OFFICE_FILTER_VALUE
            ? filteredData.filter((employee) => !employee.branchOfficeCode)
            : filteredData.filter((employee) => employee.branchOfficeCode === currentValue)
      } else if (key === 'startDate') {
        const startDateAfter = currentValue.dateAfter
        const startDateBefore = currentValue.dateBefore
        if (startDateAfter && startDateAfter !== '') {
          filteredData = filteredData.filter((employee) => employee.replyEntryDate >= startDateAfter)
        }
        if (startDateBefore && startDateBefore !== '') {
          filteredData = filteredData.filter((employee) => employee.replyEntryDate <= startDateBefore)
        }
      } else if (key === 'endDate') {
        const endDateAfter = currentValue.dateAfter
        const endDateBefore = currentValue.dateBefore

        if (endDateAfter && endDateAfter !== '') {
          filteredData = filteredData.filter((employee) => employee.contractValidTo >= endDateAfter)
        }
        if (endDateBefore && endDateBefore !== '') {
          filteredData = filteredData.filter((employee) => employee.contractValidTo <= endDateBefore)
        }
      } else if (key === 'salary' || key === 'targetBonus') {
        const lower = currentValue.lowerLimit
        const upper = currentValue.upperLimit

        if (lower !== undefined && lower.toString() !== '') {
          filteredData = filteredData.filter((employee) => {
            const monetaryValue = employee[key]
            if (monetaryValue !== null && monetaryValue !== undefined) {
              return monetaryValue.amount ? monetaryValue.amount >= lower : monetaryValue >= lower
            }
            return false
          })
        }
        if (upper !== undefined && upper.toString() !== '') {
          filteredData = filteredData.filter((employee) => {
            const monetaryValue = employee[key]
            if (monetaryValue !== null && monetaryValue !== undefined) {
              return monetaryValue.amount ? monetaryValue.amount <= upper : monetaryValue <= upper
            }
            return false
          })
        }
      } else if (key === 'resultScore') {
        filteredData = filteredData.filter((employee) => employee[key] === currentValue)
      } else if (key === 'careerPathEvaluation') {
        filteredData = filteredData.filter((employee) => employee[key] && employee[key].summary === currentValue)
      } else if (key === 'currency') {
        filteredData = filteredData.filter(
          (employee) => employee['currencyForPayments'] && employee['currencyForPayments'] === currentValue,
        )
      }
    }
  })

  return filteredData
}

export const filterSubordinatesEmployeesFromInfoBoxFilters = (
  employees: SubordinateEmployeeTableData[],
  employeeFilter: EmployeeInfoBoxFilter,
): SubordinateEmployeeTableData[] => {
  let result = employees
  if (!!employeeFilter.skillStatus)
    result = result.filter((e) => {
      const employeeSkillStatus: SkillEmployeeStatusEnum = !!e.skillStatus ? e.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
      if (
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.UnknownYellow
      ) {
        return (
          employeeSkillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
          employeeSkillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
          employeeSkillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
          employeeSkillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
          employeeSkillStatus === SkillEmployeeStatusEnum.UnknownYellow
        )
      }
      return employeeSkillStatus === employeeFilter.skillStatus
    })
  if (!!employeeFilter.careerPath)
    result = result.filter((e) => e.careerPathEvaluation && e.careerPathEvaluation.summary === employeeFilter.careerPath)
  if (!!employeeFilter.employeeLevel) result = result.filter((e) => e.contractLevel === employeeFilter.employeeLevel)
  if (!!employeeFilter.bu) result = result.filter((e) => e.businessUnit.name === employeeFilter.bu)

  return result
}

export const filterCompanySettingsEmployeesFromInfoBoxFilters = (
  employees: CompanySettingsEmployeeTableData[],
  employeeFilter: EmployeeInfoBoxFilter,
): CompanySettingsEmployeeTableData[] => {
  let result = employees
  if (!!employeeFilter.skillStatus)
    result = result.filter((e) => {
      const employeeSkillStatus: SkillEmployeeStatusEnum = !!e.skillStatus ? e.skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown
      if (
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
        employeeFilter.skillStatus === SkillEmployeeStatusEnum.UnknownYellow
      ) {
        return (
          employeeSkillStatus === SkillEmployeeStatusEnum.CvIncomplete ||
          employeeSkillStatus === SkillEmployeeStatusEnum.NearlyExpiring ||
          employeeSkillStatus === SkillEmployeeStatusEnum.PendingAiReview ||
          employeeSkillStatus === SkillEmployeeStatusEnum.RequiresImprovement ||
          employeeSkillStatus === SkillEmployeeStatusEnum.UnknownYellow
        )
      }
      return employeeSkillStatus === employeeFilter.skillStatus
    })
  if (!!employeeFilter.careerPath)
    result = result.filter((e) => e.careerPathEvaluation && e.careerPathEvaluation.summary === employeeFilter.careerPath)
  if (!!employeeFilter.employeeLevel) result = result.filter((e) => e.currentLevel === employeeFilter.employeeLevel)
  if (!!employeeFilter.bu) result = result.filter((e) => e.buName === employeeFilter.bu)

  return result
}

export const noFilterOnEmployeesApplied = (currentFilter: EmployeeInfoBoxFilter, modalFilterActive: boolean): boolean => {
  if (modalFilterActive) return false
  if (currentFilter.careerPath !== employeeTabInitialFilter.careerPath) return false
  if (currentFilter.skillStatus !== employeeTabInitialFilter.skillStatus) return false
  if (currentFilter.employeeLevel !== employeeTabInitialFilter.employeeLevel) return false
  if (currentFilter.bu !== employeeTabInitialFilter.bu) return false

  return true
}

export interface EmployeeInfoBoxFilter {
  skillStatus?: SkillEmployeeStatusEnum
  careerPath?: CareerPathEvaluationState
  employeeLevel?: EmployeeLevelEnum
  bu?: string
}

export const employeeTabInitialFilter: EmployeeInfoBoxFilter = {
  skillStatus: undefined,
  careerPath: undefined,
  employeeLevel: undefined,
  bu: undefined,
}

export const getOldestSyncUTCDate = (employees: { skillStatus?: SkillEmployeeStatusDto | null }[]): string | undefined => {
  const filteredEmployees = employees.filter((e) => !!e.skillStatus)
  if (filteredEmployees.length === 0) return undefined
  return filteredEmployees
    .map((e) => e.skillStatus!.latestSyncDate)
    .reduce((minDate, currentDate) => (currentDate < minDate ? currentDate : minDate))
}
